import { setProductConfig } from '../../../actions/v3Order';
import {
  productNeedsBackgroundSelection,
  productOffersCropOptions,
} from '../products/productConfigPredicates';
import { personalizationDeferredToCheckout } from '../../../utils/personalizationOffer';
import { nextSlotState } from '../../../reducers/v3Order/slotState';

// Every flow opens on the product picker: selectConfigFlowPages seeds `pages`
// with it and only ever appends (pose, background), so this is always pages[0].
// Routing to it means "this slot is done, start the next one" — which is why it
// is the fallback wherever a step is skipped off the end of the list.
const PRODUCT_STEP = 'product';

// The prompts that close a slot out once its picking steps are done: crop
// options (prepay only), then personalization, then on to the next slot.
//
// A slot reaches this either by finishing the last page in `pages` or by
// SKIPPING every page it had left — a child with no background to pick still
// owes these prompts. Hanging them off "the current step is the last page" is
// what dropped them: a skip jumps straight past the last page, so the test never
// fired and the customer lost the crop and personalization prompts entirely
// (and a missing required personalization then blocks checkout).
//
// `index` is the slot being COMPLETED, not the one after it. A caller whose skip
// already advanced the index has to hand back the original.
const completeSlot = ({
  index,
  product,
  productConfig,
  event,
  skipsPersonalization,
}) => {
  // An empty slot owes nothing. Reachable when the customer lands on
  // `/v3/products/productOptions/<slot>` for a slot with no product — a stale
  // URL or the back button — which productOptions.js answers with its own
  // `!product` auto-skip, calling straight back into here. Without this the
  // prompts read an undefined product: crops resolve to false, and
  // `skipsPersonalization` is false because an absent personalization_status
  // is not `no_personalization`, so an unconfigured slot is sent to the
  // personalization page.
  if (!product) return { nextUrl: PRODUCT_STEP, index: index + 1 };

  // `event` is the shoot slice (configScreen passes state.shoot under that
  // name). Prepay and "not already collected" are this flow's own conditions;
  // the rest of the rule is shared — it used to be spelled out here with
  // `poses_count > 0` in place of the individual-record test, which routed pure
  // group products to a crop page the other two flows refuse them.
  const shouldShowCropOptions =
    event?.isPrepay &&
    productOffersCropOptions(product, event) &&
    !productConfig[index]?.cropOptions;

  if (shouldShowCropOptions) return { nextUrl: 'cropOptions', index };

  if (!skipsPersonalization && !productConfig[index]?.personalization) {
    return { nextUrl: 'personalization', index };
  }

  return { nextUrl: PRODUCT_STEP, index: index + 1 };
};

// The steps a slot has no use for, so routing walks to the following page
// instead — and PRODUCT_STEP as the result means the skip ran off the end of
// `pages`, i.e. the slot is finished and owes its closing prompts.
//
// The two steps are skipped for DIFFERENT reasons; see each entry. Do not
// generalise one onto the other.
//
// Listed in flow order and applied in that order, because skipping one step can
// land on another that is itself skippable (a poseless child on a green-screen
// shoot skips pose ONTO background, then skips background too).
//
// This lives here, taking the step list and the slot's product, because there are
// two doors into the middle of the flow: the ordinary product -> next-page walk,
// and the productOptions step, which re-derives the same next page after the
// options form. They each used to carry their own copy, and they drifted — the
// productOptions copy was never given the pose skip, so a poseless child that
// happened to have product options was routed to an empty pose picker while the
// same child without options advanced correctly.
const SKIPPABLE_STEPS = [
  // Poses come from the student's gallery, so a product with no pose slots
  // genuinely opens an EMPTY picker the customer cannot advance past.
  //
  // Strict === 0: an absent poses_count is unknown, not zero, and must still be
  // offered the step. (backgroundSlotsCount, by contrast, reads absent as zero —
  // that asymmetry is deliberate and predates this helper.)
  { page: 'pose', nothingToPick: (product) => product?.poses_count === 0 },
  {
    // NOT the same reason as pose, and not an empty picker: Background.tsx
    // renders `shoot.backgroundOptions` — the shoot's catalogue — and never
    // reads the product, so a child with no background slots is shown the FULL
    // grid and can pick from it happily.
    //
    // It is skipped because the product has no background to composite: the
    // choice has nowhere to go. Nothing downstream refuses it either — the
    // selection is stored on the slot, counted by the completion gates, shipped
    // to the lab by buildCartObject, and can be billed as a background upgrade.
    // That stray tap is why team-print order items carry background codes today
    // (ENG-2300), and why Order#missing_green_screen_images? read those codes as
    // proof the order was complete.
    //
    // So the failure this prevents is a wrong order, not a stuck customer. Do
    // not widen this rule on the strength of a picker that would have rendered
    // fine.
    page: 'background',
    nothingToPick: (product) => !productNeedsBackgroundSelection(product),
  },
];

const skipUnpickableSteps = ({ nextUrl, index, pages, product }) => {
  let wrappedToNextSlot = false;

  SKIPPABLE_STEPS.forEach(({ page, nothingToPick }) => {
    if (nextUrl !== page || !nothingToPick(product)) return;

    nextUrl = pages[pages.indexOf(page) + 1] || PRODUCT_STEP;
    if (nextUrl === PRODUCT_STEP) {
      index += 1;
      wrappedToNextSlot = true;
    }
  });

  return { nextUrl, index, wrappedToNextSlot };
};

export const getNextUrl = (
  current,
  pages,
  history,
  productConfig,
  event,
  itemBeingAdded = null,
) => {
  const currentUrlIndex = pages.indexOf(current);
  const url = history.location.pathname.split('/');
  let nextUrl = pages[currentUrlIndex + 1];
  let index = parseInt(url[url.length - 1]);

  const currentProduct = productConfig[index]?.product;

  const productForStep =
    current === PRODUCT_STEP ? itemBeingAdded : currentProduct;

  // `event` is the shoot slice. The second clause is the shared policy rule —
  // this flow suppresses the prompt because PreCartOffers / PreDashboardOffers
  // show it later; see utils/personalizationOffer.
  const skipsPersonalization =
    productForStep?.personalization_status === 'no_personalization' ||
    personalizationDeferredToCheckout(event);

  // Special handling for product -> productOptions transition
  if (current === PRODUCT_STEP && itemBeingAdded) {
    const hasOptions =
      itemBeingAdded.product_option_sets?.length > 0 ||
      itemBeingAdded.product_options?.length > 0;

    if (hasOptions) {
      nextUrl = 'productOptions';
      const nextHash = '';
      url.splice(url.length - 2, 2);
      const result = `${url.join('/')}/${nextUrl}/${index}${nextHash}`;
      return result;
    }
  }

  // If we're on productOptions, continue to the next page in the normal flow
  if (current === 'productOptions') {
    // Find what would have been the next page after 'product'
    const productIndex = pages.indexOf(PRODUCT_STEP);
    nextUrl = pages[productIndex + 1] || PRODUCT_STEP;
    let wrappedToNextSlot = false;

    // Increment index if we're going back to product (next product in sequence)
    if (nextUrl === PRODUCT_STEP) {
      index += 1;
      wrappedToNextSlot = true;
    }

    // Same skips as the ordinary walk below, from the other door into the flow.
    const skipped = skipUnpickableSteps({
      nextUrl,
      index,
      pages,
      product: productForStep,
    });
    nextUrl = skipped.nextUrl;
    index = skipped.index;
    wrappedToNextSlot = wrappedToNextSlot || skipped.wrappedToNextSlot;

    // Nothing left to pick for this slot, so its closing prompts are due before
    // the next one starts.
    if (wrappedToNextSlot) {
      ({ nextUrl, index } = completeSlot({
        index: index - 1,
        product: productForStep,
        productConfig,
        event,
        skipsPersonalization,
      }));
    }

    // Same rule as the cropOptions branch and the ordinary walk below: routing to
    // the product step means "this slot is done, start the next one", and on
    // MOBILE the '#slots' hash is what shows the slot rail instead of the product
    // grid (shouldShowSlots in configScreen). Without it a customer who finishes a
    // slot through the options form lands on the picker with no rail and no
    // "Done, Add to Cart" — while the completion copy tells them to press it.
    // This branch is only reachable for PRODUCT_STEP via a skip, which is why it
    // went unnoticed. (The hasOptions return above hardcodes '' correctly: its
    // nextUrl is always 'productOptions'.)
    const nextHash = nextUrl === PRODUCT_STEP ? '#slots' : '';
    url.splice(url.length - 2, 2);
    const result = `${url.join('/')}/${nextUrl}/${index}${nextHash}`;
    return result;
  }

  // If we're on cropOptions, continue to personalization or wrap to product
  if (current === 'cropOptions') {
    nextUrl =
      !skipsPersonalization && !productConfig[index]?.personalization
        ? 'personalization'
        : PRODUCT_STEP;
    if (nextUrl !== 'personalization') index += 1;
    const nextHash = nextUrl === PRODUCT_STEP ? '#slots' : '';
    url.splice(url.length - 2, 2);
    const result = `${url.join('/')}/${nextUrl}/${index}${nextHash}`;
    return result;
  }

  // Skips that run the slot off the end of `pages` leave the next URL pointing at
  // the FOLLOWING slot's first page, so this slot's closing prompts are still
  // owed — `wrappedToNextSlot` is what the completion test below reads.
  //
  // productNeedsBackgroundSelection (inside the helper), not
  // productSkipsBackgroundSelection: the completion gate
  // (selectBackgroundsSatisfied) owes a background from exactly the children this
  // routing offers a page to, and the two must ask the same question. Asking for a
  // background the flow refuses to collect is what left the parent permanently
  // incomplete in ENG-2630.
  const skipped = skipUnpickableSteps({
    nextUrl,
    index,
    pages,
    product: productForStep,
  });
  nextUrl = skipped.nextUrl;
  index = skipped.index;
  const { wrappedToNextSlot } = skipped;

  // Two ways a slot finishes picking: it completed the last page, or a skip took
  // it past every page it had left. Both owe the same closing prompts.
  // `productForStep` is itemBeingAdded on the 'product' step, since productConfig
  // is not updated until after this call.
  if (currentUrlIndex === pages.length - 1 || wrappedToNextSlot) {
    ({ nextUrl, index } = completeSlot({
      index: wrappedToNextSlot ? index - 1 : index,
      product: productForStep,
      productConfig,
      event,
      skipsPersonalization,
    }));
  }

  if (currentUrlIndex < 0) index += 1;

  const nextHash = nextUrl === PRODUCT_STEP ? '#slots' : '';
  url.splice(url.length - 2, 2);

  const result = `${url.join('/')}/${nextUrl}/${index}${nextHash}`;
  return result;
};

/**
 * Get the array of poses for a slot from productConfig (supports both .pose and .poses).
 */
export const getPosesForSlot = (config) => {
  if (!config) return [];
  if (Array.isArray(config.poses)) return config.poses;
  return config.pose ? [config.pose] : [];
};

/**
 * Get required pose count for a slot: 0 if pose step is skipped globally, else product.poses_count ?? 1.
 */
export const getRequiredPoseCountForSlot = (config, pages) => {
  if (!pages.includes('pose')) return 0;
  const product = config?.product;
  if (!product) return 0;
  const count = product.poses_count;
  return typeof count === 'number' ? count : 1;
};

export const addToConfig = ({
  dispatch,
  history,
  productConfig,
  pages,
  itemToAdd,
  event,
  cropIndex = null,
}) => {
  const { pathname, hash } = history.location;
  const currentUrlSplit = pathname.split('/');
  const current = currentUrlSplit[currentUrlSplit.length - 2];
  const index = parseInt(pathname.split('/').pop().replace('#slots', ''), 10);
  let newItem = itemToAdd;

  if (cropIndex !== null && current === 'pose') {
    newItem = { ...itemToAdd };
    newItem.selected_crop = newItem.cropped_images[cropIndex];
  }

  // Route on what the reducer will actually STORE, not on the state captured
  // before the dispatch. They are the same transformation (nextSlotState), and
  // deriving them apart is what let completeSlot skip the crop prompt for a
  // replacement whose cropOptions the reducer had just cleared.
  const routingConfig = (itemToStore) => ({
    ...productConfig,
    [index]: nextSlotState(productConfig[index], itemToStore),
  });

  let item = { [current]: newItem };

  if (pathname.includes('product/') && hash !== '#slots')
    item.usedSlotSelectionUi = true;

  // If we're on cropOptions page, merge the selections with the existing product config
  if (current === 'cropOptions') {
    const existingConfig = productConfig[index] || {};
    const mergedItem = { ...existingConfig, cropOptions: newItem };
    dispatch(setProductConfig({ index, itemToAdd: mergedItem }));
    history.push(
      getNextUrl(
        current,
        pages,
        history,
        routingConfig(mergedItem),
        event,
        null,
      ),
    );
    return;
  }

  // If we're on productOptions page, merge the options with the existing product config
  if (current === 'productOptions') {
    const existingConfig = productConfig[index] || {};
    const mergedItem = { ...existingConfig, productOptions: newItem };
    dispatch(setProductConfig({ index, itemToAdd: mergedItem }));
    history.push(
      getNextUrl(
        current,
        pages,
        history,
        routingConfig(mergedItem),
        event,
        current === PRODUCT_STEP ? newItem : null,
      ),
    );
    return;
  }

  // Pose step: support multiple poses per product (poses_count === 2) and skip when poses_count === 0
  if (current === 'pose') {
    const existingConfig = productConfig[index] || {};
    const product = existingConfig.product;
    const posesCount =
      typeof product?.poses_count === 'number' ? product.poses_count : 1;

    if (posesCount === 0) {
      // Product requires no pose; advance without storing
      history.push(
        getNextUrl(current, pages, history, productConfig, event, null),
      );
      return;
    }

    if (posesCount >= 2) {
      const existingPoses = getPosesForSlot(existingConfig);
      const newPoses = [...existingPoses, newItem].slice(0, posesCount);
      item = { poses: newPoses };
      dispatch(setProductConfig({ index, itemToAdd: item }));
      // Only advance to next step when we have selected enough poses for this slot
      if (newPoses.length >= posesCount) {
        history.push(
          getNextUrl(current, pages, history, routingConfig(item), event, null),
        );
      }
      return;
    }
  }

  dispatch(setProductConfig({ index, itemToAdd: item }));
  history.push(
    getNextUrl(
      current,
      pages,
      history,
      routingConfig(item),
      event,
      current === PRODUCT_STEP ? newItem : null,
    ),
  );
};
